import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSun, FiMoon, FiMenu, FiX, FiArrowUp } from 'react-icons/fi';
import { ThemeContext } from '../../contexts/ThemeContext';
import logo from '../../assets/images/logo.png';
import useTextScramble from '../../hooks/useTextScramble';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Handle scroll effect
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleScrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Text scramble hooks for nav links
  const homeScramble = useTextScramble('Home');
  const aboutScramble = useTextScramble('About');
  const projectsScramble = useTextScramble('Projects');
  const servicesScramble = useTextScramble('Services');
  const blogScramble = useTextScramble('Blog');
  const contactScramble = useTextScramble('Contact');

  return (
    <>
      <NavbarContainer scrolled={scrolled} className={theme}>
        <div className="container">
          <NavbarContent>
            <Logo to="/">
              <LogoImage src={logo} alt="Prince Yadav Logo" />
            </Logo>

            <DesktopMenu>
              <NavLinks>
                <NavItem>
                  <NavLink to="/" $isActive={location.pathname === '/'}>
                    <span ref={homeScramble.ref} onMouseEnter={homeScramble.onMouseEnter}>Home</span>
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/about" $isActive={location.pathname === '/about'}>
                    <span ref={aboutScramble.ref} onMouseEnter={aboutScramble.onMouseEnter}>About</span>
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/projects" $isActive={location.pathname === '/projects'}>
                    <span ref={projectsScramble.ref} onMouseEnter={projectsScramble.onMouseEnter}>Projects</span>
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/services" $isActive={location.pathname === '/services'}>
                    <span ref={servicesScramble.ref} onMouseEnter={servicesScramble.onMouseEnter}>Services</span>
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/blog" $isActive={location.pathname === '/blog'}>
                    <span ref={blogScramble.ref} onMouseEnter={blogScramble.onMouseEnter}>Blog</span>
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/contact" $isActive={location.pathname === '/contact'}>
                    <span ref={contactScramble.ref} onMouseEnter={contactScramble.onMouseEnter}>Contact</span>
                  </NavLink>
                </NavItem>
              </NavLinks>
            </DesktopMenu>

            <NavActions>
              <ThemeToggle onClick={toggleTheme}>
                {theme === 'light' ? <FiMoon size={20} /> : <FiSun size={20} />}
              </ThemeToggle>

              <MobileMenuButton onClick={() => setIsOpen(!isOpen)}>
                {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
              </MobileMenuButton>
            </NavActions>
          </NavbarContent>
        </div>

        <AnimatePresence>
          {isOpen && (
            <MobileMenu
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className={theme}
            >
              <MobileNavLinks>
                <MobileNavItem>
                  <MobileNavLink to="/" onClick={() => setIsOpen(false)}>
                    <span>Home</span>
                  </MobileNavLink>
                </MobileNavItem>
                <MobileNavItem>
                  <MobileNavLink to="/about" onClick={() => setIsOpen(false)}>
                    <span>About</span>
                  </MobileNavLink>
                </MobileNavItem>
                <MobileNavItem>
                  <MobileNavLink to="/projects" onClick={() => setIsOpen(false)}>
                    <span>Projects</span>
                  </MobileNavLink>
                </MobileNavItem>
                <MobileNavItem>
                  <MobileNavLink to="/services" onClick={() => setIsOpen(false)}>
                    <span>Services</span>
                  </MobileNavLink>
                </MobileNavItem>
                <MobileNavItem>
                  <MobileNavLink to="/blog" onClick={() => setIsOpen(false)}>
                    <span>Blog</span>
                  </MobileNavLink>
                </MobileNavItem>
                <MobileNavItem>
                  <MobileNavLink to="/contact" onClick={() => setIsOpen(false)}>
                    <span>Contact</span>
                  </MobileNavLink>
                </MobileNavItem>
              </MobileNavLinks>
            </MobileMenu>
          )}
        </AnimatePresence>
      </NavbarContainer>

      <AnimatePresence>
        {scrolled && (
          <ScrollTopBtn
            onClick={handleScrollTop}
            initial={{ opacity: 0, scale: 0.5, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 20 }}
            transition={{ duration: 0.25 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Scroll to top"
          >
            <FiArrowUp size={20} />
          </ScrollTopBtn>
        )}
      </AnimatePresence>
    </>
  );
};

const NavbarContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--header-height);
  z-index: 1000;
  transition: all 0.3s ease;
  backdrop-filter: ${props => props.scrolled ? 'blur(var(--glass-blur))' : 'none'};
  -webkit-backdrop-filter: ${props => props.scrolled ? 'blur(var(--glass-blur))' : 'none'};
  box-shadow: ${props => props.scrolled ? 'var(--glass-shadow)' : 'none'};
  border-bottom: ${props => props.scrolled ? '1px solid var(--glass-border)' : '1px solid transparent'};
  
  &.light {
    background-color: ${props => props.scrolled ? 'var(--glass-bg)' : 'transparent'};
  }
  
  &.dark {
    background-color: ${props => props.scrolled ? 'var(--glass-bg)' : 'transparent'};
  }
`;

const NavbarContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  z-index: 1001;
`;

const LogoImage = styled.img`
  height: clamp(60px, 10vw, 80px);
  width: auto;
  transition: all 0.3s ease;
  
  .dark & {
    filter: invert(1) drop-shadow(0 0 8px rgba(124, 58, 237, 0.3));
    opacity: 0.95;
  }
`;

const DesktopMenu = styled.nav`
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLinks = styled.ul`
  display: flex;
  list-style: none;
`;

const NavItem = styled.li`
  margin: 0 1rem;
`;

const NavLink = styled(Link)`
  position: relative;
  font-size: 1rem;
  font-weight: 500;
  padding: 0.5rem 0;
  transition: color 0.2s ease;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(135deg, var(--primary-light), var(--secondary-light));
    transform-origin: left;
    transform: scaleX(${props => props.$isActive ? 1 : 0});
    transition: transform 0.2s ease;
  }
  
  &:hover::after {
    transform: scaleX(1);
  }
  
  /* no transform on hover to avoid glyph hinting flicker */
  
  .light & {
    color: var(--text-primary-light);
  }
  
  .dark & {
    color: var(--text-primary-dark);
    
    &::after {
      background: linear-gradient(135deg, var(--primary-dark), var(--secondary-dark));
    }
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
`;

const ThemeToggle = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  .light & {
    color: var(--text-primary-light);
    
    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
  
  .dark & {
    color: var(--text-primary-dark);
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }
`;

const MobileMenuButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: none;
  z-index: 1001;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .light & {
    color: var(--text-primary-light);
  }
  
  .dark & {
    color: var(--text-primary-dark);
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  padding: 6rem 2rem 2rem;
  z-index: 1000;
  overflow-y: auto;
  backdrop-filter: blur(var(--glass-blur));
  -webkit-backdrop-filter: blur(var(--glass-blur));
  
  &.light {
    background-color: rgba(255, 255, 255, 0.98);
  }
  
  &.dark {
    background-color: rgba(5, 5, 15, 0.98);
  }
`;

const MobileNavLinks = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
`;

const MobileNavItem = styled.li`
  width: 100%;
  text-align: center;
`;

const MobileNavLink = styled(Link)`
  font-size: 1.5rem;
  font-weight: 600;
  padding: 0.5rem 0;
  display: block;
  
  .light & {
    color: var(--text-primary-light);
  }
  
  .dark & {
    color: var(--text-primary-dark);
  }
`;

const ScrollTopBtn = styled(motion.button)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #7C3AED, #2563EB);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 999;
  box-shadow: 0 4px 20px rgba(124, 58, 237, 0.5);
  @media (max-width: 768px) { bottom: 1.2rem; right: 1.2rem; width: 42px; height: 42px; }
`;

export default Navbar;